import type {FC} from 'react';
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
  Autocomplete,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import type {AccountsCheckinsListParams, CheckinOption} from 'api/v2';
import {Hooks} from '@wellstone-solutions/common';
import {
  Box,
  Button,
  Icon,
  IconButton,
  IconNames,
  Select,
  Stack,
  TextField,
  Typography,
} from '@wellstone-solutions/web';
import {FilterDialog} from 'modules/datagrid';
import {DayPickerCalendar} from 'components/DayPickerCalendar/DayPickerCalendar';
import type {DateRange as DateRangeType} from 'react-day-picker';
import {ActiveCount} from 'components/ActiveCount/ActiveCount';
import {checkinsQueryOptions} from 'queries/checkins';
import {Theme} from '@wellstone-solutions/common';
import moment from 'moment';

interface FilterFormProps {
  onSubmit: (values: AccountsCheckinsListParams) => void;
}

interface FormValues {
  checkinOptions: CheckinOption[];
  dateRange: {createdAfter: string; createdBefore: string} | string;
  operator?: 'and' | 'or';
}

const formatDateRange = (start: moment.Moment, end: moment.Moment) => ({
  createdAfter: start.startOf('day').utc().format(),
  createdBefore: end.endOf('day').utc().format(),
});

const dateRangeItems = [
  {
    label: 'Today',
    value: formatDateRange(moment(), moment()),
  },
  {
    label: 'Yesterday',
    value: formatDateRange(
      moment().subtract(1, 'day'),
      moment().subtract(1, 'day'),
    ),
  },
  {
    label: 'Last 7 days',
    value: formatDateRange(moment().subtract(7, 'days'), moment()),
  },
  {
    label: 'Last 14 days',
    value: formatDateRange(moment().subtract(14, 'days'), moment()),
  },
  {
    label: 'Last 30 days',
    value: formatDateRange(moment().subtract(30, 'days'), moment()),
  },
  {
    label: 'Last 60 days',
    value: formatDateRange(moment().subtract(60, 'days'), moment()),
  },
  {
    label: 'Last 90 days',
    value: formatDateRange(moment().subtract(90, 'days'), moment()),
  },
  {
    label: 'Custom date range',
    value: 'custom-date-range',
  },
];

export const FilterForm: FC<FilterFormProps> = ({onSubmit}) => {
  const checkinsOptionsQuery = useQuery(checkinsQueryOptions.optionsList());

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customDateRange, setCustomDateRange] = useState<DateRangeType | null>(
    null,
  );
  const initialValues: FormValues = {
    checkinOptions: [],
    dateRange: '',
    operator: 'or',
  };

  const form = Hooks.useForm({
    initialValues,
    onSubmit: (values: FormValues) => {
      const filterParams: AccountsCheckinsListParams = {};

      if (values.checkinOptions.length > 0) {
        const statesList = values.checkinOptions
          .map((option: CheckinOption) => option.value)
          .join(',');

        filterParams.states = statesList;
        filterParams.operator = values.operator;
      }

      if (typeof values.dateRange === 'object') {
        filterParams.createdAfter = values.dateRange.createdAfter;
        filterParams.createdBefore = values.dateRange.createdBefore;
      } else if (
        form.values.dateRange === 'custom-date-range' &&
        customDateRange?.to &&
        customDateRange?.from
      ) {
        const formattedRange = formatDateRange(
          moment(customDateRange.from),
          moment(customDateRange.to),
        );
        filterParams.createdAfter = formattedRange.createdAfter;
        filterParams.createdBefore = formattedRange.createdBefore;
      }
      onSubmit(filterParams);
    },
  });

  const isCustomDateRange = form.values.dateRange === 'custom-date-range';

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReset = () => {
    if (isCustomDateRange) {
      setCustomDateRange(null);
    }
    form.resetForm({
      values: initialValues,
    });
    form.handleSubmit();
  };

  const handleDateRangeChange = (range: DateRangeType | null) => {
    setCustomDateRange(range);
    if (range && range.to && range.from) {
      form.handleSubmit();
    }
  };

  const activeFilterCount =
    form.values.checkinOptions.length +
    (typeof form.values.dateRange === 'object' || customDateRange ? 1 : 0);

  return (
    <Box>
      {activeFilterCount > 0 && (
        <Button
          variant="text"
          size="small"
          sx={{mr: 2, px: 2}}
          onClick={handleReset}>
          Clear Filters
        </Button>
      )}

      <Box sx={{position: 'relative', display: 'inline-block'}}>
        <ActiveCount
          count={activeFilterCount}
          sx={{
            position: 'absolute',
            top: -10,
            left: -12,
            zIndex: 1,
            height: 20,
            backgroundColor: Theme.colorPalette.secondary,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={{px: 2}}
          startIcon={<Icon name={IconNames.Filter} size={20} />}
          onClick={(event) => {
            setAnchorEl(event.currentTarget.parentElement);
          }}>
          Filter
        </Button>
      </Box>

      <FilterDialog anchorEl={anchorEl} onClose={handleClose}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Typography variant="h5" sx={{fontWeight: 500}}>
            Filters
          </Typography>
          <IconButton
            onClick={handleClose}
            icon={IconNames.Xmark}
            iconProps={{name: IconNames.Xmark}}
            data-testid="close-dialog"
          />
        </Stack>

        <Stack direction="column" spacing={2} sx={{mt: 2, mb: 2}}>
          <Typography variant="subtitle1" sx={{fontWeight: 600}}>
            Filter by Date
          </Typography>
          <Select
            label="Date range"
            items={dateRangeItems}
            value={form.values.dateRange}
            onChange={(event) => {
              form.setFieldTouched('dateRange');
              form.setFieldValue('dateRange', event.target.value);
              if (event.target.value !== 'custom-date-range') {
                setCustomDateRange(null);
                form.handleSubmit();
              }
            }}
            canUnselect={true}
            containerProps={{
              sx: {width: '100%', mb: 2},
            }}
          />

          {form.values.dateRange === 'custom-date-range' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}>
              {customDateRange?.from && customDateRange?.to && (
                <>
                  <Typography
                    variant="overline"
                    sx={{
                      alignItems: 'flex-start',
                      ml: 8,
                      width: '100%',
                      color: Theme.colorPalette.outline,
                    }}>
                    Selected
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      fontWeight: 400,
                      ml: 8,
                      alignItems: 'flex-start',
                      width: '100%',
                    }}>
                    {moment(customDateRange.from).format('ddd, MMM D, YYYY')} -{' '}
                    {moment(customDateRange.to).format('ddd, MMM D, YYYY')}
                  </Typography>
                </>
              )}
              <DayPickerCalendar
                selected={customDateRange}
                onSelect={handleDateRangeChange}
              />
            </Box>
          )}

          <Box>
            <FormControl>
              <FormLabel
                id="operator-label"
                sx={{fontWeight: 700, color: 'darkest.main'}}>
                Filter by Check In terms
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="operator-label"
                name="operator"
                value={form.values.operator || 'or'}
                sx={{px: 1, my: 1}}
                onChange={(event) => {
                  form.setFieldTouched('operator');
                  form.setFieldValue('operator', event.target.value);
                  form.handleSubmit();
                }}>
                <FormControlLabel
                  value="or"
                  control={<Radio />}
                  label="Contains any"
                />
                <FormControlLabel
                  value="and"
                  control={<Radio />}
                  label="Contains all"
                />
              </RadioGroup>
            </FormControl>

            <Autocomplete
              multiple
              options={checkinsOptionsQuery.data?.results || []}
              getOptionLabel={(option) => option.title}
              value={form.values.checkinOptions}
              disabled={checkinsOptionsQuery.isLoading}
              renderInput={(params) => (
                <TextField {...params} label="Check in terms" />
              )}
              onChange={(event, value) => {
                form.setFieldTouched('checkinOptions');
                form.setFieldValue('checkinOptions', value);
                form.handleSubmit();
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            disabled={!form.dirty}
            onClick={handleReset}>
            Reset
          </Button>
          <Button variant="contained" fullWidth onClick={handleClose}>
            Apply
          </Button>
        </Stack>
      </FilterDialog>
    </Box>
  );
};
